import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
  FiShoppingCart,
  FiFileText,
  FiCheckCircle,
  FiXCircle,
  FiEye,
  FiCalendar,
  FiUser,
  FiClock,
  FiPackage,
  FiPauseCircle,
  FiPlayCircle,
  FiDownload,
  FiChevronDown,
  FiChevronRight,
  FiChevronLeft,
  FiEdit3,
  FiCheck,
  FiAlertCircle,
  FiAlertTriangle,
  FiFilter,
  FiSearch,
  FiTrash2
} from 'react-icons/fi';
import { LoadingGrid } from '../../components/UI/Loading';
import DownloadReportModal from '../../components/UI/Modal/DownloadReportModal';
import { Modal } from '../../components/UI/Modal';
import ConfirmModal from '../../components/UI/Modal/ConfirmModal';
import OrderTypeModal from '../../components/UI/Modal/OrderTypeModal';
import { ordenesService, ordenUtils } from '../../services/ordenesService';
import { reportesService } from '../../services';
import { useDebounce } from '../../hooks/useDebounce';
import { useAuth } from '../../contexts/AuthContext';
import './Ventas.scss';

// Orden y nombres iguales a los que aparecen en el selector de Productos.
const PICKING_CATEGORIES = [
  ['CAPSULES', 'Cápsulas', '#c62828', '#ffebee'],
  ['POWDERS', 'Polvos', '#2e7d32', '#e8f5e9'],
  ['LIQUIDS', 'Líquidos', '#1565c0', '#e3f2fd'],
  ['OILS', 'Aceites', '#b45309', '#fff3e0'],
  ['TEAS', 'Infusiones', '#8d6e00', '#fff8cf'],
  ['CREAMS', 'Cremas', '#7b1fa2', '#f3e5f5'],
  ['LEAVES', 'Hojas', '#00695c', '#e0f2f1']
];
// Para categorías añadidas en el futuro: color estable y nombre legible.
// Nunca mezclarlas bajo "Otros" cuando sí tienen una categoría asignada.
const FUTURE_CATEGORY_COLORS = [
  ['#ad1457', '#fce4ec'], ['#4527a0', '#ede7f6'],
  ['#00695c', '#e0f2f1'], ['#5d4037', '#efebe9'],
  ['#455a64', '#eceff1'], ['#00838f', '#e0f7fa'],
  ['#558b2f', '#f1f8e9'], ['#6d4c41', '#efebe9']
];
const categoryInfo = (category) => {
  const code = String(category || 'OTHER').trim().toUpperCase();
  const known = PICKING_CATEGORIES.find(([key]) => key === code);
  if (known) return known;
  if (code === 'OTHER') return ['OTHER', 'Otros / Sin categoría', '#616161', '#f5f5f5'];
  const hash = [...code].reduce((sum, char) => (sum * 31 + char.charCodeAt(0)) >>> 0, 0);
  const [color, background] = FUTURE_CATEGORY_COLORS[hash % FUTURE_CATEGORY_COLORS.length];
  const label = code.toLocaleLowerCase('es').replace(/_/g, ' ').replace(/^./, letter => letter.toLocaleUpperCase('es'));
  return [code, label, color, background];
};
const pickingItems = (items, status) => {
  if (!['PROCESSING', 'COMPLETED'].includes(status)) return items;
  const rank = (item) => {
    const index = PICKING_CATEGORIES.findIndex(([key]) => key === categoryInfo(item.product_category)[0]);
    return index === -1 ? PICKING_CATEGORIES.length : index;
  };
  return [...items].sort((a, b) => rank(a) - rank(b) ||
    categoryInfo(a.product_category)[1].localeCompare(categoryInfo(b.product_category)[1], 'es'));
};

const initialsFor = (name) => String(name || '').trim().split(/\s+/).filter(Boolean).slice(0, 2).map(part => part[0].toUpperCase()).join('') || '?';
const PER_PAGE = 25;

const Ventas = () => {
  const navigate = useNavigate();
  const { hasPermission, user } = useAuth();
  const canCreateSale = hasPermission('ventas.create');
  const canDeleteSale = hasPermission('ventas.delete');
  // Las etapas sin permiso no se muestran al usuario.
  const stagePermissions = {
    detenidas: 'ventas.stage.on_hold',
    procesando: 'ventas.stage.processing',
    completadas: 'ventas.stage.completed'
  };
  const hasStageRestrictions = Object.values(stagePermissions).some(permission => hasPermission(permission));
  const canViewStage = (stage) => !hasStageRestrictions || hasPermission(stagePermissions[stage]);
  const visibleStages = Object.keys(stagePermissions).filter(canViewStage);

  const [ordenes, setOrdenes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [initialLoad, setInitialLoad] = useState(true); // solo la 1ra carga muestra pantalla completa
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('preordenes'); // 'preordenes', 'ordenes', 'cotizaciones', 'todas'
  // Los permisos por etapa habilitan trabajar en esa etapa, no solo verla.
  const canManageStage = (stage) =>
    hasStageRestrictions
      ? hasPermission(stagePermissions[stage])
      : hasPermission('ventas.update');
  const canManageOrder = (orden) => {
    if (!orden) return false;
    // Los permisos de etapa corresponden exclusivamente a preórdenes.
    // Las órdenes normales conservan sus permisos independientes.
    if (orden.order_type === 'ORDER') {
      return hasPermission('ventas.orders.update') || hasPermission('ventas.update');
    }
    const stageByStatus = { ON_HOLD: 'detenidas', RELEASED: 'detenidas', PROCESSING: 'procesando', COMPLETED: 'completadas' };
    const stage = stageByStatus[orden.status];
    return stage ? canManageStage(stage) : hasPermission('ventas.update');
  };
  // Conservar la pestaña de la que se salió al editar una preorden.
  // Al regresar de Actualizar o Cancelar, no volver a Detenidas por defecto.
  const [subFilter, setSubFilter] = useState(() => {
    const saved = window.sessionStorage.getItem('erp:ventas:preorder-stage');
    return ['detenidas', 'procesando', 'completadas'].includes(saved)
      ? saved : 'detenidas';
  });
  useEffect(() => {
    window.sessionStorage.setItem('erp:ventas:preorder-stage', subFilter);
  }, [subFilter]);
  // La autorización de cada registro depende de su estado real, nunca de la pestaña seleccionada.
  useEffect(() => {
    if (!canViewStage(subFilter) && visibleStages.length > 0) {
      setSubFilter(visibleStages[0]);
    }
  }, [subFilter, hasStageRestrictions]);

  const [filterSource, setFilterSource] = useState('ALL'); // 'ALL', 'BETEL_NATURAL', 'NATURALAND_LAB', 'BETEL_NATURAL_VIERNES'
  const [filterStatus, setFilterStatus] = useState('ALL'); // Estado (varía según tab)
  const [filterDateFrom, setFilterDateFrom] = useState(''); // YYYY-MM-DD
  const [filterDateTo, setFilterDateTo] = useState('');     // YYYY-MM-DD
  const [filterClient, setFilterClient] = useState('');     // Texto contains contra nombre cliente
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [processingOrders, setProcessingOrders] = useState(new Set());
  const [showReportModal, setShowReportModal] = useState(false);
  const [isDownloadingReport, setIsDownloadingReport] = useState(false);
  const [showOrderTypeModal, setShowOrderTypeModal] = useState(false);
  const [expandedOrderId, setExpandedOrderId] = useState(null);
  const [expandedOrderDetails, setExpandedOrderDetails] = useState({});
  const [processingParticipants, setProcessingParticipants] = useState({});

  // Sincronización de respaldo cada 2 s para los estados guardados por otros compañeros.
  // El servidor expira participantes tras 45 s sin actividad.
  useEffect(() => {
    const orderId = expandedOrderId;
    if (!orderId || expandedOrderDetails[orderId]?.status !== 'PROCESSING') return undefined;
    let cancelled = false;
    const refresh = async () => {
      if (document.visibilityState === 'hidden') return;
      const [presence, details] = await Promise.all([
        ordenesService.processingPresence(orderId),
        ordenesService.getOrdenById(orderId)
      ]);
      if (cancelled) return;
      if (presence.success) setProcessingParticipants(prev => ({ ...prev, [orderId]: presence.data?.participants || [] }));
      if (details.success) setExpandedOrderDetails(prev => ({ ...prev, [orderId]: details.data }));
    };
    refresh();
    const timer = window.setInterval(refresh, 2000);
    return () => { cancelled = true; window.clearInterval(timer); };
  }, [expandedOrderId, expandedOrderDetails[expandedOrderId]?.status]);

  const [loadingDetails, setLoadingDetails] = useState(false);
  const [internalNotesDrafts, setInternalNotesDrafts] = useState({});
  const [savingInternalNotes, setSavingInternalNotes] = useState(null);
  const [editingInternalNotes, setEditingInternalNotes] = useState({});

  const saveInternalStageNotes = async (order) => {
    if (!canManageOrder(order) || !['ON_HOLD', 'RELEASED', 'PROCESSING', 'COMPLETED'].includes(order.status)) return;
    const value = internalNotesDrafts[order.id] ?? order.internal_notes ?? '';
    setSavingInternalNotes(order.id);
    try {
      const result = await ordenesService.updateOrden(order.id, { internal_notes: value });
      if (!result.success) throw new Error(result.message || 'No se pudieron guardar las notas internas');
      setExpandedOrderDetails(prev => ({
        ...prev, [order.id]: { ...(prev[order.id] || order), internal_notes: value }
      }));
      setEditingInternalNotes(prev => ({ ...prev, [order.id]: false }));
      setSuccess('Notas Internas guardadas');
    } catch (err) {
      setError(err.message || 'Error al guardar las notas internas');
    } finally {
      setSavingInternalNotes(null);
    }
  };

  // Paginación server-side
  const [page, setPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const pageRef = useRef(1);

  // Confirmación genérica (reemplaza window.confirm / prompt)
  const [confirm, setConfirm] = useState(null);
  const [confirmReason, setConfirmReason] = useState('');
  const acceptConfirm = async () => {
    const c = confirm;
    const reason = confirmReason;
    setConfirm(null);
    setConfirmReason('');
    if (c?.onConfirm) await c.onConfirm(reason);
  };

  // Estados para modal de conversión a ORDER
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviewerName, setReviewerName] = useState('');
  const [orderToConvert, setOrderToConvert] = useState(null);
  const [isConverting, setIsConverting] = useState(false);

  const [showSourceDropdown, setShowSourceDropdown] = useState(false);
  const [isMobileFilter, setIsMobileFilter] = useState(() => window.innerWidth <= 768);
  useEffect(() => {
    const onResize = () => setIsMobileFilter(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const sourceDropdownRef = useRef(null);

  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  const sourceFuentes = [
    { id: 'ALL',                    label: 'Todas las fuentes' },
    { id: 'BETEL_NATURAL',          label: 'Betel Natural'     },
    { id: 'NATURALAND_LAB',         label: 'Natural&Lab'       },
    { id: 'BETEL_NATURAL_VIERNES',  label: 'Betel Viernes'     },
  ];

  // Opciones de Estado disponibles según el tab activo
  // PRE-ÓRDENES no incluye Estado: ya está cubierto por los sub-tabs (DETENIDAS/LIBERADAS/PROCESANDO/COMPLETADAS)
  const STATUS_OPTIONS_BY_TAB = {
    preordenes: [],
    ordenes: [
      { value: 'PENDING_APPROVAL', label: 'Pendiente Aprobación' },
      { value: 'APPROVED', label: 'Aprobada' },
      { value: 'RELEASED', label: 'Liberada' },
      { value: 'CONVERTED', label: 'Convertida' },
      { value: 'REJECTED', label: 'Rechazada' },
      { value: 'CLOSED_WON', label: 'Ganada' },
      { value: 'CLOSED_LOST', label: 'Perdida' },
    ],
    cotizaciones: [
      { value: 'DRAFT', label: 'Borrador' },
      { value: 'ESTIMATE', label: 'Estimado' },
      { value: 'SENT', label: 'Enviada' },
      { value: 'NEGOTIATION', label: 'Negociación' },
      { value: 'APPROVED', label: 'Aprobada' },
      { value: 'REJECTED', label: 'Rechazada' },
      { value: 'CLOSED_LOST', label: 'Perdida' },
    ],
    todas: [
      { value: 'PENDING_APPROVAL', label: 'Pendiente Aprobación' },
      { value: 'APPROVED', label: 'Aprobada' },
      { value: 'RELEASED', label: 'Liberada' },
      { value: 'ON_HOLD', label: 'Detenida' },
      { value: 'PROCESSING', label: 'Procesando' },
      { value: 'COMPLETED', label: 'Completada' },
      { value: 'CONVERTED', label: 'Convertida' },
      { value: 'REJECTED', label: 'Rechazada' },
      { value: 'CLOSED_WON', label: 'Ganada' },
      { value: 'CLOSED_LOST', label: 'Perdida' },
      { value: 'ESTIMATE', label: 'Estimado' },
      { value: 'SENT', label: 'Enviada' },
      { value: 'NEGOTIATION', label: 'Negociación' },
    ],
  };

  // Qué filtros muestra cada tab. Source no aplica a COTIZACIONES.
  const FILTERS_BY_TAB = {
    preordenes: { source: true, status: false, date: true, client: true },
    ordenes:    { source: true, status: true,  date: true, client: true },
    cotizaciones: { source: false, status: true, date: true, client: true },
    todas:      { source: true, status: true,  date: true, client: true },
  };

  const activeTabFilters = FILTERS_BY_TAB[typeFilter] || FILTERS_BY_TAB.todas;
  const statusOptions = STATUS_OPTIONS_BY_TAB[typeFilter] || [];

  // Cuenta filtros activos (excluye search; ése tiene su propio input)
  const activeFiltersCount =
    (activeTabFilters.source && filterSource !== 'ALL' ? 1 : 0) +
    (activeTabFilters.status && filterStatus !== 'ALL' ? 1 : 0) +
    (activeTabFilters.date && (filterDateFrom || filterDateTo) ? 1 : 0) +
    (activeTabFilters.client && filterClient.trim() ? 1 : 0);

  const clearAllFilters = () => {
    setFilterSource('ALL');
    setFilterStatus('ALL');
    setFilterDateFrom('');
    setFilterDateTo('');
    setFilterClient('');
  };

  // Resetea filtros incompatibles cuando cambia el tab (ej. status no aplica a PRE-ÓRDENES)
  useEffect(() => {
    if (!activeTabFilters.source && filterSource !== 'ALL') setFilterSource('ALL');
    if (!activeTabFilters.status && filterStatus !== 'ALL') setFilterStatus('ALL');
    // Si el estado seleccionado ya no está disponible en el nuevo tab, limpiarlo
    if (activeTabFilters.status && filterStatus !== 'ALL' && !statusOptions.some(s => s.value === filterStatus)) {
      setFilterStatus('ALL');
    }
    // eslint-disable-next-line
  }, [typeFilter]);

  // Wave SVG dinámica
  const menuRef    = useRef(null);
  const sec1Ref    = useRef(null); // NUEVA ORDEN
  const sec2Ref    = useRef(null); // BÚSQUEDA
  const sec3Ref    = useRef(null); // FUENTES
  const svgPathRef = useRef(null);

  const buildWavePath = useCallback(() => {
    if (!menuRef.current || !sec2Ref.current || !sec3Ref.current || !svgPathRef.current) return;
    const menuRect = menuRef.current.getBoundingClientRect();
    const W = menuRect.width;
    if (W === 0) return;

    const VB   = 1000;
    const TOP  = 3;
    const BOT  = 53;
    const CW   = 36;
    const half = CW / 2;
    const scale = VB / W;

    // Sin permiso para crear, la primera sección no existe: iniciar la curva
    // en el borde izquierdo en lugar de conservar el trazo fijo original.
    const b1 = sec1Ref.current
      ? (sec1Ref.current.getBoundingClientRect().right - menuRect.left) * scale
      : 0;
    const b2 = (sec2Ref.current.getBoundingClientRect().right - menuRect.left) * scale;
    const b3 = (sec3Ref.current.getBoundingClientRect().right - menuRect.left) * scale;

    const d = [
      `M 0,${TOP}`,
      `L ${Math.max(0, b1 - half)},${TOP}`,
      `C ${Math.max(0, b1)},${TOP} ${Math.max(0, b1)},${BOT} ${b1 + half},${BOT}`,
      `L ${b2 - half},${BOT}`,
      `C ${b2},${BOT} ${b2},${TOP} ${b2 + half},${TOP}`,
      `L ${VB},${TOP}`,
    ].join(' ');

    svgPathRef.current.setAttribute('d', d);
  }, []);

  useEffect(() => {
    if (!menuRef.current) return;
    buildWavePath();
    const t = setTimeout(buildWavePath, 100);
    const ro = new ResizeObserver(buildWavePath);
    [menuRef, sec1Ref, sec2Ref, sec3Ref].forEach(ref => {
      if (ref.current) ro.observe(ref.current);
    });
    return () => { clearTimeout(t); ro.disconnect(); };
  }, [buildWavePath, isLoading]);

  // Cierre del dropdown de fuentes al hacer clic fuera
  useEffect(() => {
    const handler = (e) => {
      if (sourceDropdownRef.current && !sourceDropdownRef.current.contains(e.target) && !e.target.closest('.ventas-mobile-filter-panel')) {
        setShowSourceDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Al cambiar cualquier filtro/búsqueda/tab se vuelve a la página 1 y se recarga
  useEffect(() => {
    setPage(1);
    pageRef.current = 1;
    loadOrdenes(1);
    // eslint-disable-next-line
  }, [typeFilter, subFilter, filterSource, filterStatus, filterDateFrom, filterDateTo, filterClient, debouncedSearchQuery]);

  // Construye los parámetros server-side según el tab activo, los filtros y la búsqueda.
  const buildServerParams = useCallback((targetPage) => {
    const params = {
      skip: (targetPage - 1) * PER_PAGE,
      limit: PER_PAGE
    };

    // Tipo (tab principal)
    if (typeFilter === 'ordenes') {
      params.order_type = 'ORDER';
    } else if (typeFilter === 'cotizaciones') {
      params.order_type = 'ESTIMATE';
    } else if (typeFilter === 'preordenes') {
      params.order_type = 'PRE_ORDER';
      // Sub-tabs de PRE-ÓRDENES → estado
      const subStatus = {
        detenidas: 'ON_HOLD',
        procesando: 'PROCESSING',
        completadas: 'COMPLETED'
      }[subFilter];
      if (subStatus) params.status = subStatus;
    } else if (typeFilter === 'todas') {
      params.exclude_pre_order = true;
    }

    // Fuente (no aplica a COTIZACIONES)
    if (activeTabFilters.source && filterSource !== 'ALL') {
      params.source = filterSource;
    }

    // Estado (PRE-ÓRDENES ya filtra por sub-tab, por eso activeTabFilters.status es false ahí)
    if (activeTabFilters.status && filterStatus !== 'ALL') {
      params.status = filterStatus;
    }

    // Rango de fechas
    if (activeTabFilters.date && filterDateFrom) params.date_from = filterDateFrom;
    if (activeTabFilters.date && filterDateTo) params.date_to = filterDateTo;

    // Búsqueda por texto (número, título, cliente) — la resuelve el backend
    // Buscar en el backend solo a partir de la 3ª letra; con 1-2 letras se
    // muestra la lista filtrada (sin búsqueda de texto). Los filtros activos
    // (fuente, estado, fechas) se envían siempre, así la búsqueda opera dentro del filtro.
    const term = (debouncedSearchQuery || '').trim();
    if (term.length >= 3) params.search = term;

    return params;
  }, [typeFilter, subFilter, filterSource, filterStatus, filterDateFrom, filterDateTo, debouncedSearchQuery, activeTabFilters]);

  const loadOrdenes = useCallback(async (pageArg) => {
    const targetPage = pageArg || pageRef.current || 1;
    setIsLoading(true);
    setError(null);

    try {
      const response = await ordenesService.getOrdenes(buildServerParams(targetPage));

      if (response.success) {
        let ordenesData = (response.data || []).map(orden => ({
          ...orden,
          type: orden.order_type || orden.type || 'ORDER',
          // El endpoint de lista trae client_name/client_company; lo normalizamos a client
          client: orden.client || {
            full_name: orden.client_name,
            company_name: orden.client_company
          }
        }));

        // Refinamiento client-side por nombre de cliente (filtro secundario del panel)
        if (activeTabFilters.client && filterClient.trim()) {
          const q = filterClient.trim().toLowerCase();
          ordenesData = ordenesData.filter(o =>
            (o.client?.company_name || '').toLowerCase().includes(q) ||
            (o.client?.full_name || '').toLowerCase().includes(q)
          );
        }

        setOrdenes(ordenesData);
        const per = response.perPage || PER_PAGE;
        setTotalItems(response.total || 0);
        setTotalPages(Math.max(1, Math.ceil((response.total || 0) / per)));
      } else {
        setError(response.message || 'Error al cargar las órdenes');
        setOrdenes([]);
      }
    } catch (err) {
      console.error('Error loading orders:', err);
      setError('Error al cargar las órdenes');
      setOrdenes([]);
    } finally {
      setIsLoading(false);
      setInitialLoad(false);
    }
  }, [buildServerParams, activeTabFilters, filterClient]);

  const changePage = (newPage) => {
    if (newPage < 1 || newPage > totalPages || newPage === page) return;
    setPage(newPage);
    pageRef.current = newPage;
    loadOrdenes(newPage);
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleClearSearch = () => {
    setSearchQuery('');
  };

  const toggleExpand = async (ordenId) => {
    // Si ya está expandido, colapsarlo
    if (expandedOrderId === ordenId) {
      setExpandedOrderId(null);
      return;
    }

    // Expandir y cargar datos si no están en cache
    setExpandedOrderId(ordenId);
    if (expandedOrderDetails[ordenId]) {
      return;
    }

    setLoadingDetails(true);
    try {
      const result = await ordenesService.getOrdenById(ordenId);
      if (result.success) {
        // Auto-fix: PRE_ORDER antigua con items sin quantity_requested
        const hasLegacyItems = result.data.items?.some(item => !item.quantity_requested);

        if (result.data.order_type === 'PRE_ORDER' && hasLegacyItems) {
          try {
            const fixResult = await ordenesService.fixLegacyItems(ordenId);

            if (fixResult.success) {
              // Recargar la orden con los datos actualizados
              const updatedResult = await ordenesService.getOrdenById(ordenId);
              if (updatedResult.success) {
                setExpandedOrderDetails(prev => ({
                  ...prev,
                  [ordenId]: updatedResult.data
                }));
                setSuccess('✅ Orden actualizada automáticamente');
                setTimeout(() => setSuccess(null), 3000);
              }
            }
          } catch (fixError) {
            console.error('Error auto-fixing legacy items:', fixError);
            // Continuar con los datos originales
            setExpandedOrderDetails(prev => ({
              ...prev,
              [ordenId]: result.data
            }));
          }
        } else {
          setExpandedOrderDetails(prev => ({
            ...prev,
            [ordenId]: result.data
          }));
        }
      } else {
        setError('Error al cargar detalles de la orden');
      }
    } catch (err) {
      console.error('Error loading order details:', err);
      setError('Error al cargar detalles de la orden');
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleApprove = (e, orden) => {
    e.stopPropagation();
    setConfirm({
      title: 'Aprobar venta',
      message: `¿Deseas aprobar la venta ${orden.number}?`,
      confirmText: 'Aprobar',
      confirmClass: 'btn-success',
      onConfirm: async () => {
        setProcessingOrders(prev => new Set(prev).add(orden.id));
        setError(null);
        try {
          const response = await ordenesService.approveOrden(orden.id);
          if (response.success) {
            setSuccess(`Venta ${orden.number} aprobada exitosamente`);
            setTimeout(() => setSuccess(null), 3000);
            await loadOrdenes();
          } else {
            setError(response.message || 'Error al aprobar la venta');
          }
        } catch (err) {
          console.error('Error approving order:', err);
          setError('Error al aprobar la venta');
        } finally {
          setProcessingOrders(prev => {
            const newSet = new Set(prev);
            newSet.delete(orden.id);
            return newSet;
          });
        }
      }
    });
  };

  const handleReject = (e, orden) => {
    e.stopPropagation();
    setConfirm({
      title: 'Rechazar venta',
      message: `¿Deseas rechazar la venta ${orden.number}?`,
      confirmText: 'Rechazar',
      confirmClass: 'btn-danger',
      withReason: true,
      reasonLabel: 'Motivo del rechazo (opcional)',
      onConfirm: async (reason) => {
        setProcessingOrders(prev => new Set(prev).add(orden.id));
        setError(null);
        try {
          const response = await ordenesService.rejectOrden(orden.id, reason || '');
          if (response.success) {
            setSuccess(`Venta ${orden.number} rechazada`);
            setTimeout(() => setSuccess(null), 3000);
            await loadOrdenes();
          } else {
            setError(response.message || 'Error al rechazar la venta');
          }
        } catch (err) {
          console.error('Error rejecting order:', err);
          setError('Error al rechazar la venta');
        } finally {
          setProcessingOrders(prev => {
            const newSet = new Set(prev);
            newSet.delete(orden.id);
            return newSet;
          });
        }
      }
    });
  };

  const handleConvertToInvoice = (e, orden) => {
    e.stopPropagation();
    setConfirm({
      title: 'Convertir a factura',
      message: `¿Deseas convertir la venta ${orden.number} en una factura?`,
      confirmText: 'Convertir',
      confirmClass: 'btn-primary',
      onConfirm: async () => {
        setProcessingOrders(prev => new Set(prev).add(orden.id));
        setError(null);
        try {
          const response = await ordenesService.convertToInvoice(orden.id);
          if (response.success) {
            setSuccess(`Venta ${orden.number} convertida a factura exitosamente`);
            setTimeout(() => setSuccess(null), 3000);
            await loadOrdenes();
            if (response.data?.invoice_id) {
              setTimeout(() => {
                navigate(`/system/facturacion/${response.data.invoice_id}`);
              }, 1500);
            }
          } else {
            setError(response.message || 'Error al convertir la venta a factura');
          }
        } catch (err) {
          console.error('Error converting to invoice:', err);
          setError('Error al convertir la venta a factura');
        } finally {
          setProcessingOrders(prev => {
            const newSet = new Set(prev);
            newSet.delete(orden.id);
            return newSet;
          });
        }
      }
    });
  };

  const handleViewOrder = (orden) => {
    navigate(`/system/ventas/${orden.id}`);
  };

  const handleEditOrden = (e, ordenId) => {
    e.stopPropagation();
    navigate(`/system/ventas/${ordenId}/editar`);
  };

  const handleEditPreorden = (e, ordenId) => {
    e.stopPropagation();
    navigate(`/system/ventas/${ordenId}/editar-preorden`);
  };

  const handleDeleteOrden = (e, orden) => {
    e.stopPropagation();
    const label = orden.number || `#${orden.id}`;
    setConfirm({
      title: 'Eliminar orden',
      message: `¿Eliminar la orden ${label}? Esta acción no se puede deshacer.`,
      confirmText: 'Eliminar',
      confirmClass: 'btn-danger',
      onConfirm: async () => {
        setProcessingOrders(prev => new Set(prev).add(orden.id));
        setError(null);
        try {
          const response = await ordenesService.deleteOrden(orden.id);
          if (response.success) {
            setSuccess(`Orden ${label} eliminada`);
            setTimeout(() => setSuccess(null), 3000);
            if (expandedOrderId === orden.id) setExpandedOrderId(null);
            await loadOrdenes();
          } else {
            setError(response.message || 'Error al eliminar la orden');
          }
        } catch (err) {
          console.error('Error deleting order:', err);
          setError('Error al eliminar la orden');
        } finally {
          setProcessingOrders(prev => {
            const newSet = new Set(prev);
            newSet.delete(orden.id);
            return newSet;
          });
        }
      }
    });
  };

  // Funciones helper
  function getThumbnailType(status) {
    switch (status) {
      case 'CONVERTED':
      case 'CLOSED_WON':
        return 'green-gradient';
      case 'PENDING_APPROVAL':
        return 'orange-gradient';
      case 'APPROVED':
      case 'RELEASED':
        return 'blue-dark-gradient';
      case 'REJECTED':
      case 'CLOSED_LOST':
        return 'gray';
      case 'ESTIMATE':
      case 'SENT':
        return 'purple-gradient';
      case 'ON_HOLD':
        return 'yellow-gradient';
      default:
        return 'blue-dark-gradient';
    }
  }

  function formatCurrency(amount) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);
  }

  function formatDate(dateString) {
    if (!dateString) return '';
    let date;
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      // Fecha sin hora (YYYY-MM-DD): interpretar como LOCAL, no UTC. Si no, en zonas
      // detrás de UTC (EE.UU.) se mostraría el día anterior (orden de hoy = "ayer").
      const [y, m, d] = dateString.split('-').map(Number);
      date = new Date(y, m - 1, d);
    } else {
      date = new Date(dateString);
    }
    return date.toLocaleDateString('es-CO', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }

  function getEstadoBadge(status) {
    const badgeClass = {
      'CONVERTED': 'status-badge--success',
      'CLOSED_WON': 'status-badge--success',
      'PENDING_APPROVAL': 'status-badge--warning',
      'APPROVED': 'status-badge--info',
      'RELEASED': 'status-badge--success',
      'ON_HOLD': 'status-badge--warning',
      'REJECTED': 'status-badge--danger',
      'CLOSED_LOST': 'status-badge--secondary',
      'DRAFT': 'status-badge--secondary',
      'ESTIMATE': 'status-badge--info',
      'SENT': 'status-badge--info',
      'NEGOTIATION': 'status-badge--warning'
    };

    const statusText = {
      'PENDING_APPROVAL': 'Pendiente Aprobación',
      'APPROVED': 'Aprobada',
      'RELEASED': 'Liberada',
      'ON_HOLD': 'Detenida',
      'REJECTED': 'Rechazada',
      'CONVERTED': 'Convertida',
      'CLOSED_WON': 'Ganada',
      'CLOSED_LOST': 'Perdida',
      'DRAFT': 'Borrador',
      'ESTIMATE': 'Estimado',
      'SENT': 'Enviada',
      'NEGOTIATION': 'Negociación'
    };

    return (
      <span className={`status-badge ${badgeClass[status] || 'status-badge--info'}`}>
        {statusText[status] || status}
      </span>
    );
  }

  function getTipoBadge(type) {
    if (type === 'ORDER' || type === 'SALE') {
      return (
        <span className="tipo-badge tipo-badge--orden">
          ORDEN
        </span>
      );
    } else if (type === 'ESTIMATE' || type === 'QUOTE') {
      return (
        <span className="tipo-badge tipo-badge--cotizacion">
          COTIZACIÓN
        </span>
      );
    } else if (type === 'PRE_ORDER') {
      return (
        <span className="tipo-badge tipo-badge--preorden">
          PRE-ORDEN
        </span>
      );
    }
    return null;
  }

  function getSourceBadge(source) {
    const sourceConfig = {
      'BETEL_NATURAL': { label: 'BETEL NATURAL', className: 'source-badge--betel' },
      'NATURALAND_LAB': { label: 'NATURALAND LAB', className: 'source-badge--naturaland' },
      'BETEL_NATURAL_VIERNES': { label: 'BETEL VIERNES', className: 'source-badge--viernes' },
      'PORTAL': { label: '🌐 PORTAL', className: 'source-badge--portal' }
    };
    const config = sourceConfig[source] || { label: source, className: '' };
    return (
      <span className={`source-badge ${config.className}`}>
        {config.label}
      </span>
    );
  }

  function getItemStatusIcon(item) {
    if (item.is_stock_available === true) {
      return <FiCheckCircle className="item-status-icon item-status-icon--available" />;
    } else if (item.is_stock_available === false) {
      return <FiAlertCircle className="item-status-icon item-status-icon--unavailable" />;
    }
    return <FiPackage className="item-status-icon item-status-icon--pending" />;
  }

  // Función para renderizar iconos de validación de shipping (para PRE_ORDER)

  // ===== FUNCIONES DE VALIDACIÓN DE PRODUCTOS =====

  // Función para manejar cambio de estado de validación de un producto
  const handleValidationChange = async (ordenId, itemId, newStatus) => {
    try {
      const result = await ordenesService.updateItemValidationStatus(ordenId, itemId, {
        validation_status: newStatus,
        ...(newStatus === 'NO_STOCK' ? { quantity_confirmed: 0 } : {})
      });

      if (result.success) {
        setSuccess(`Estado cambiado a ${getStatusLabel(newStatus)}`);
        setTimeout(() => setSuccess(null), 2000);

        if (newStatus === 'NO_STOCK') {
          setTempQuantities(prev => {
            const next = { ...prev };
            delete next[`${ordenId}-${itemId}`];
            return next;
          });
        }

        // Recargar detalles de la orden
        const ordenData = await ordenesService.getOrdenById(ordenId);
        if (ordenData.success) {
          setExpandedOrderDetails(prev => ({
            ...prev,
            [ordenId]: ordenData.data
          }));
        }
      } else {
        setError(result.message || 'Error al actualizar validación');
      }
    } catch (error) {
      console.error('Error updating validation:', error);
      setError('Error al actualizar el estado de validación');
    }
  };

  // Estado temporal para cantidades confirmadas (mientras el usuario escribe)
  const [tempQuantities, setTempQuantities] = useState({});

  // Función para manejar cambio en el input de cantidad confirmada (mientras escribe)
  const handleQuantityConfirmedChange = (ordenId, itemId, value) => {
    const key = `${ordenId}-${itemId}`;
    setTempQuantities(prev => ({ ...prev, [key]: value }));
  };

  // Función para guardar la cantidad confirmada cuando el usuario sale del input (onBlur)
  const handleQuantityConfirmedBlur = async (ordenId, itemId, value) => {
    const key = `${ordenId}-${itemId}`;
    const quantity = parseFloat(value);

    if (isNaN(quantity) || quantity < 0) {
      setError('Cantidad inválida');
      // Limpiar el valor temporal inválido
      setTempQuantities(prev => { const n = { ...prev }; delete n[key]; return n; });
      return;
    }

    // Validar que no exceda la cantidad solicitada
    const currentItem = expandedOrderDetails[ordenId]?.items?.find(i => i.id === itemId);
    const maxQty = parseFloat(currentItem?.quantity_requested ?? currentItem?.quantity ?? 0);
    if (maxQty > 0 && quantity > maxQty) {
      setError(`La cantidad confirmada no puede superar la solicitada (${maxQty})`);
      setTempQuantities(prev => { const n = { ...prev }; delete n[key]; return n; });
      return;
    }

    // Lógica automática de estado según cantidad:
    // - Si cantidad es 0 → automáticamente NO_STOCK
    // - Si cantidad > 0 y estaba en NO_STOCK → volver a PENDING para que el usuario confirme
    // - Si cantidad > 0 y estaba en otro estado → mantener el estado actual
    let validationStatus = currentItem?.validation_status || 'PENDING';
    let autoMessage = null;

    if (quantity === 0) {
      validationStatus = 'NO_STOCK';
      autoMessage = 'Cantidad en 0 - Marcado automáticamente como Sin Stock';
    } else if (quantity > 0 && currentItem?.validation_status === 'NO_STOCK') {
      validationStatus = 'PENDING';
      autoMessage = 'Cantidad actualizada - Cambiado a Pendiente para validación';
    }

    try {
      const result = await ordenesService.updateItemValidationStatus(ordenId, itemId, {
        validation_status: validationStatus,
        quantity_confirmed: quantity
      });

      if (result.success) {
        const message = autoMessage || 'Cantidad confirmada actualizada';
        setSuccess(message);
        setTimeout(() => setSuccess(null), 2000);

        // Limpiar el valor temporal ya que ahora viene del servidor
        setTempQuantities(prev => { const n = { ...prev }; delete n[key]; return n; });

        // Recargar detalles de la orden
        const ordenData = await ordenesService.getOrdenById(ordenId);
        if (ordenData.success) {
          setExpandedOrderDetails(prev => ({
            ...prev,
            [ordenId]: ordenData.data
          }));
        }
      } else {
        setError(result.message || 'Error al actualizar cantidad');
      }
    } catch (error) {
      console.error('Error updating quantity:', error);
      setError('Error al actualizar la cantidad confirmada');
    }
  };

  // Función para obtener label de estado
  function getStatusLabel(status) {
    const labels = {
      'NO_STOCK': 'Sin Stock',
      'AVAILABLE': 'Disponible',
      'REVIEWED': 'Revisado',
      'PENDING': 'Pendiente'
    };
    return labels[status] || status;
  }

  // Función para arreglar órdenes antiguas (inicializar quantity_requested)
  const handleFixLegacyItems = async (ordenId) => {
    setLoadingDetails(true);
    try {
      const result = await ordenesService.fixLegacyItems(ordenId);

      if (result.success) {
        setSuccess(`✅ Orden actualizada: ${result.data.items_fixed} productos arreglados`);
        setTimeout(() => setSuccess(null), 3000);

        // Recargar detalles de la orden
        const ordenData = await ordenesService.getOrdenById(ordenId);
        if (ordenData.success) {
          setExpandedOrderDetails(prev => ({
            ...prev,
            [ordenId]: ordenData.data
          }));
        }

        // Recargar lista de órdenes
        await loadOrdenes();
      } else {
        setError(result.message || 'Error al actualizar orden');
      }
    } catch (error) {
      console.error('Error fixing legacy items:', error);
      setError('Error al actualizar la orden');
    } finally {
      setLoadingDetails(false);
    }
  };

  // Función para validar automáticamente todos los productos de una orden
  const handleValidateAllItems = (ordenId) => {
    setConfirm({
      title: 'Validar productos',
      message: '¿Validar automáticamente todos los productos contra el inventario?',
      confirmText: 'Validar',
      confirmClass: 'btn-primary',
      onConfirm: async () => {
        setLoadingDetails(true);
        try {
          const result = await ordenesService.validateAllItems(ordenId);
          if (result.success) {
            setSuccess(`Validación completada: ${result.data.items_validated} productos validados`);
            setTimeout(() => setSuccess(null), 3000);

            // Recargar detalles de la orden
            const ordenData = await ordenesService.getOrdenById(ordenId);
            if (ordenData.success) {
              setExpandedOrderDetails(prev => ({
                ...prev,
                [ordenId]: ordenData.data
              }));
            }

            // Recargar lista de órdenes
            await loadOrdenes();
          } else {
            setError(result.message || 'Error al validar productos');
          }
        } catch (error) {
          console.error('Error validating items:', error);
          setError('Error al validar los productos');
        } finally {
          setLoadingDetails(false);
        }
      }
    });
  };

  function getItemValidationButtons(validationStatus, itemId, ordenId, item) {
    const order = expandedOrderDetails[ordenId];
    const isProcessing = order?.status === 'PROCESSING';
    const isCompleted = order?.status === 'COMPLETED';
    const canEdit = canManageOrder(order);
    // El estado REVIEWED reemplaza al de Procesando; recuperar el anterior
    // de la anotación persistida al revisar, o mostrar el estado actual.
    const processingMatch = (item.validation_notes || '').match(/\[ESTADO_PROCESANDO:(PENDING|NO_STOCK|AVAILABLE)\]/);
    const processingStatus = validationStatus === 'REVIEWED'
      ? (processingMatch?.[1] || (item.is_stock_available === true ? 'AVAILABLE' : item.is_stock_available === false ? 'NO_STOCK' : null))
      : validationStatus;
    const processingLabels = { PENDING: 'Pendiente', NO_STOCK: 'Sin stock', AVAILABLE: 'Disponible' };
    const statuses = [
      { value: 'PENDING', icon: 'P', label: 'Pendiente', title: 'Pendiente de validar' },
      { value: 'NO_STOCK', icon: '✗', label: 'Sin stock', title: 'Sin stock disponible' },
      { value: 'AVAILABLE', icon: '✓', label: 'Disponible', title: 'Stock disponible' },
      { value: 'REVIEWED', icon: '✓✓', label: 'Revisado', title: 'Revisado y confirmado' }
    ].filter(status => isCompleted || status.value !== 'REVIEWED');

    if (isCompleted) {
      // La selección del primer responsable se muestra como información,
      // sin apariencia de botón editable.
      return (
        <div className="validation-status-buttons" style={{ alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
          <span style={{ padding: '7px 12px', borderRadius: 7, background: '#f2ebf5', color: '#6f258b', fontWeight: 600 }}>
            Procesando: {processingLabels[processingStatus] || 'Sin registro'}
          </span>
          <button
            className={`validation-btn validation-btn--reviewed ${validationStatus === 'REVIEWED' ? 'validation-btn--selected' : ''}`}
            type="button"
            disabled={!canEdit || getItemQty(item) <= 0}
            aria-pressed={validationStatus === 'REVIEWED'}
            onClick={(e) => {
              e.stopPropagation();
              if (!canEdit) return;
              handleValidationChange(ordenId, itemId,
                validationStatus === 'REVIEWED' ? (processingStatus || 'AVAILABLE') : 'REVIEWED');
            }}
            title={validationStatus === 'REVIEWED' ? 'Quitar revisión' : 'Marcar como revisado'}
            style={{ opacity: 1, minWidth: 112, cursor: canEdit ? 'pointer' : 'not-allowed' }}
          >
            <span className="validation-btn__icon">✓✓</span>
            <span className="validation-btn__label">Revisado</span>
          </button>
        </div>
      );
    }

    return (
      <div className="validation-status-buttons">
        {statuses.map(status => {
          const isSelected = validationStatus === status.value;
          const actionable = canEdit && isProcessing;
          return (
            <button
              key={status.value}
              className={`validation-btn validation-btn--${status.value.toLowerCase()} ${isSelected ? 'validation-btn--selected' : ''} ${!actionable ? 'validation-btn--disabled' : ''}`}
              onClick={(e) => {
                e.stopPropagation();
                if (actionable) handleValidationChange(ordenId, itemId, status.value);
              }}
              disabled={!actionable}
              title={status.title}
            >
              <span className="validation-btn__icon">{status.icon}</span>
              <span className="validation-btn__label">{status.label}</span>
              {isSelected && item.validated_by && (
                <span title={`Revisado por ${item.validated_by}${item.validated_at ? ' · ' + new Date(item.validated_at).toLocaleString('es') : ''}`}
                  style={{ marginLeft: 5, fontSize: 11, fontWeight: 700, borderRadius: 5, background: '#f2ebf5', color: '#6f258b', padding: '2px 5px' }}>
                  {initialsFor(item.validated_by)}
                </span>
              )}
            </button>
          );
        })}
      </div>
    );
  }

  function canApprove(orden) {
    return orden && orden.status === 'PENDING_APPROVAL';
  }
  // ===== HANDLERS PARA CAMBIO DE ESTADO DE PRE-ORDERS =====

  // Cambiar estado de Detenida directamente a Procesando
  const handleEnProceso = (e, orden) => {
    e.stopPropagation();
    setConfirm({
      title: 'Pasar a En Proceso',
      message: `¿Pasar la orden ${orden.number} a En Proceso?`,
      confirmText: 'En Proceso',
      confirmClass: 'btn-primary',
      onConfirm: async () => {
        setProcessingOrders(prev => new Set(prev).add(orden.id));
        setError(null);
        try {
          const response = await ordenesService.setProcessingOrden(orden.id);
          if (response.success) {
            setSuccess(`Orden ${orden.number} en proceso`);
            setTimeout(() => setSuccess(null), 3000);
            setTypeFilter('preordenes');
            setSubFilter('procesando');
            // El efecto de filtros carga la lista de la pestaña de destino.
            

            // Si la orden está expandida, actualizar su cache de detalles
            if (expandedOrderId === orden.id) {
              const ordenData = await ordenesService.getOrdenById(orden.id);
              if (ordenData.success) {
                setExpandedOrderDetails(prev => ({
                  ...prev,
                  [orden.id]: ordenData.data
                }));
              }
            }
          } else {
            setError(response.message || 'Error al cambiar el estado');
          }
        } catch (err) {
          console.error('Error setting processing:', err);
          setError('Error al cambiar el estado');
        } finally {
          setProcessingOrders(prev => {
            const newSet = new Set(prev);
            newSet.delete(orden.id);
            return newSet;
          });
        }
      }
    });
  };

  // Cambiar estado de En Proceso a Completada
  const handleCompletar = (e, orden) => {
    e.stopPropagation();

    // Validación frontend: bloquear si no se cumplen las condiciones
    if (!canCompleteOrder(orden)) {
      setError(getCanCompleteReason(orden) || 'No se puede completar la orden en este momento.');
      return;
    }

    const detailData = expandedOrderDetails[orden.id];
    const items = detailData?.items || [];
    const zeroQtyItems = items.filter(item => getItemQty(item) === 0);

    const message = zeroQtyItems.length > 0
      ? `Los productos con cantidad 0 (${zeroQtyItems.map(i => i.description || i.product_name || `ID ${i.id}`).join(', ')}) no se enviarán y quedarán registrados en las notas de la orden y factura. ¿Desea continuar?`
      : `¿Enviar la orden ${orden.number} a Revisión Final?`;

    setConfirm({
      title: 'Enviar a revisión final',
      message,
      confirmText: 'Completar',
      confirmClass: 'btn-success',
      onConfirm: async () => {
        setProcessingOrders(prev => new Set(prev).add(orden.id));
        setError(null);
        try {
          // El backend excluye de forma atómica los productos no enviados y
          // agrega la nota al mismo campo de notas de la orden.
          // Completar la orden
          const response = await ordenesService.completePreOrden(orden.id);
          if (response.success) {
            setSuccess(`Orden ${orden.number} completada exitosamente`);
            setTimeout(() => setSuccess(null), 3000);
            setTypeFilter('preordenes');
            setSubFilter('completadas');
            // El efecto de filtros carga la lista de la pestaña de destino.
            

            // Limpiar cache de detalles de la orden completada
            if (expandedOrderId === orden.id) {
              const ordenData = await ordenesService.getOrdenById(orden.id);
              if (ordenData.success) {
                setExpandedOrderDetails(prev => ({
                  ...prev,
                  [orden.id]: ordenData.data
                }));
              }
            }
          } else {
            setError(response.message || 'Error al completar la orden');
          }
        } catch (err) {
          console.error('Error completing order:', err);
          setError('Error al completar la orden');
        } finally {
          setProcessingOrders(prev => {
            const newSet = new Set(prev);
            newSet.delete(orden.id);
            return newSet;
          });
        }
      }
    });
  };

  // Cambiar estado a Detenida (desde cualquier estado)
  const handleDetener = (e, orden) => {
    e.stopPropagation();
    setConfirm({
      title: 'Detener orden',
      message: `¿Detener la orden ${orden.number}?`,
      confirmText: 'Detener',
      confirmClass: 'btn-warning',
      withReason: true,
      reasonLabel: 'Motivo para detener (opcional)',
      onConfirm: async (reason) => {
        setProcessingOrders(prev => new Set(prev).add(orden.id));
        setError(null);
        try {
          const response = await ordenesService.holdOrden(orden.id, reason || '');
          if (response.success) {
            setSuccess(`Orden ${orden.number} detenida`);
            setTimeout(() => setSuccess(null), 3000);
            await loadOrdenes();

            // Si la orden está expandida, actualizar su cache de detalles
            if (expandedOrderId === orden.id) {
              const ordenData = await ordenesService.getOrdenById(orden.id);
              if (ordenData.success) {
                setExpandedOrderDetails(prev => ({
                  ...prev,
                  [orden.id]: ordenData.data
                }));
              }
            }
          } else {
            setError(response.message || 'Error al detener la orden');
          }
        } catch (err) {
          console.error('Error holding order:', err);
          setError('Error al detener la orden');
        } finally {
          setProcessingOrders(prev => {
            const newSet = new Set(prev);
            newSet.delete(orden.id);
            return newSet;
          });
        }
      }
    });
  };

  // Convertir PRE_ORDER completada a ORDER
  const handleConvertToOrder = async (e, orden) => {
    e.stopPropagation();

    if (!canConvertCompletedOrder(orden)) {
      setError('En Revisión Final, todos los productos enviados deben estar Revisados antes de convertir a Orden. Expande la preorden para verificar.');
      return;
    }
    // Abrir modal para pedir nombre de quien revisó
    setOrderToConvert(orden);
    // Identificar automáticamente a quien convierte la preorden en orden.
    setReviewerName(user?.full_name?.trim() || '');
    setShowReviewModal(true);
  };

  const handleConfirmConversion = async () => {
    if (!reviewerName.trim()) {
      setError('Por favor ingrese el nombre de quien revisó la orden');
      return;
    }

    if (!orderToConvert) return;

    setIsConverting(true);
    setProcessingOrders(prev => new Set(prev).add(orderToConvert.id));
    setError(null);

    try {
      const response = await ordenesService.convertPreOrderToOrder(
        orderToConvert.id,
        { reviewed_by: reviewerName.trim() }
      );

      if (response.success) {
        setSuccess(
          `PRE_ORDER ${orderToConvert.number} convertida exitosamente a ORDER ${response.data.number}`
        );
        setTimeout(() => setSuccess(null), 5000);

        // Cerrar modal
        setShowReviewModal(false);
        setReviewerName('');
        setOrderToConvert(null);

        await loadOrdenes();

        // Cerrar el detalle expandido
        setExpandedOrderId(null);
        setExpandedOrderDetails({});

        // Cambiar a la pestaña de ÓRDENES para ver la nueva orden creada
        setTypeFilter('ordenes');
      } else {
        setError(response.message || 'Error al convertir la PRE_ORDER');
      }
    } catch (err) {
      console.error('Error converting pre-order:', err);
      setError('Error al convertir la PRE_ORDER a ORDER');
    } finally {
      setIsConverting(false);
      setProcessingOrders(prev => {
        const newSet = new Set(prev);
        newSet.delete(orderToConvert.id);
        return newSet;
      });
    }
  };

  // Función helper para renderizar botones de acción según el estado
  // Helper: obtener cantidad efectiva de un ítem
  function getItemQty(item) {
    return parseFloat(item.quantity_confirmed ?? item.quantity_requested ?? item.quantity ?? 0);
  }

  // Verifica si una orden PROCESSING puede pasar a COMPLETADA.
  // Reglas:
  //  1. Todos los ítems con qty > 0 deben estar en REVIEWED (✓✓)
  //  2. Si todos los ítems tienen qty = 0 → bloquear (nada que completar)
  //  3. Ítems con qty = 0 serán eliminados automáticamente antes de completar
  function canCompleteOrder(orden) {
    const details = expandedOrderDetails[orden.id];
    if (!details?.items?.length) return false;
    const shipped = details.items.filter(item => getItemQty(item) > 0);
    return shipped.length > 0 && shipped.every(item =>
      ['AVAILABLE', 'REVIEWED'].includes(item.validation_status)
    );
  }

  function getCanCompleteReason(orden) {
    const details = expandedOrderDetails[orden.id];
    if (!details?.items?.length) return 'Expande la orden para cargar los detalles';
    const shipped = details.items.filter(item => getItemQty(item) > 0);
    if (!shipped.length) return 'No hay productos disponibles para enviar';
    const pending = shipped.filter(item => !['AVAILABLE', 'REVIEWED'].includes(item.validation_status));
    return pending.length ? `${pending.length} producto(s) pendientes de marcar Disponible` : '';
  }

  function canConvertCompletedOrder(orden) {
    const details = expandedOrderDetails[orden.id];
    if (!details?.items?.length) return false;
    const shipped = details.items.filter(item => getItemQty(item) > 0);
    return shipped.length > 0 && shipped.every(item => item.validation_status === 'REVIEWED');
  }

  function getActionButton(orden) {
    if (!canManageOrder(orden)) return null;
    const isProcessing = processingOrders.has(orden.id);

    // Disponible en todos los estados facturables (liberada, en proceso y completada)
    const facturarBtn = canConvertToInvoice(orden) ? (
      <button
        className="btn-estado btn-estado--facturar"
        onClick={(e) => handleConvertToInvoice(e, orden)}
        disabled={isProcessing}
        title="Convertir a factura"
      >
        <FiFileText size={16} /> Facturar
      </button>
    ) : null;

    // Permitir editar preórdenes en todas las etapas, respetando permisos de etapa.
    const editarPreordenBtn = orden.order_type === 'PRE_ORDER' ? (
      <button
        className="btn btn--sm btn--ghost"
        onClick={(e) => handleEditPreorden(e, orden.id)}
        disabled={isProcessing}
        title="Editar pre-orden"
      >
        <FiEdit3 />
      </button>
    ) : null;

    switch (orden.status) {
      case 'APPROVED': // Aprobada
      case 'ON_HOLD': // Detenida
        return (
          <div className="acciones-grupo">
            <button
              className="btn-estado btn-estado--proceso"
              onClick={(e) => handleEnProceso(e, orden)}
              disabled={isProcessing}
              title="Pasar a Procesando"
            >
              <FiClock size={16} /> Procesando
            </button>
            {editarPreordenBtn}
          </div>
        );

      case 'RELEASED': // Liberada
        return (
          <div className="acciones-grupo">
            {editarPreordenBtn}
            <button
              className="btn-estado btn-estado--proceso"
              onClick={(e) => handleEnProceso(e, orden)}
              disabled={isProcessing}
              title="Pasar a En Proceso"
            >
              <FiClock size={16} /> En Proceso
            </button>
            {facturarBtn}
            <button
              className="btn-estado btn-estado--detener"
              onClick={(e) => handleDetener(e, orden)}
              disabled={isProcessing}
              title="Detener orden"
            >
              <FiPauseCircle size={14} />
            </button>
          </div>
        );

      case 'PROCESSING': { // En Proceso
        const canComplete = canCompleteOrder(orden);
        const completeTitle = !canComplete
          ? getCanCompleteReason(orden)
          : 'Completar orden';
        return (
          <div className="acciones-grupo">
            <button
              className="btn-estado btn-estado--completar"
              onClick={(e) => handleCompletar(e, orden)}
              disabled={isProcessing || !canComplete}
              title={completeTitle}
            >
              <FiCheckCircle size={16} /> Completar
            </button>
            {editarPreordenBtn}
            {facturarBtn}
            <button
              className="btn-estado btn-estado--detener"
              onClick={(e) => handleDetener(e, orden)}
              disabled={isProcessing}
              title="Detener orden"
            >
              <FiPauseCircle size={14} />
            </button>
          </div>
        );
      }

      case 'COMPLETED': // Completada
        return (
          <div className="acciones-grupo">
            <button
              className="btn-estado btn-estado--convertir"
              onClick={(e) => handleConvertToOrder(e, orden)}
              disabled={isProcessing || !canConvertCompletedOrder(orden)}
              title="Convertir a ORDER"
            >
              <FiCheckCircle size={16} /> Convertir a ORDER
            </button>
            {editarPreordenBtn}
            {facturarBtn}
            <span className="estado-final estado-final--small">
              <FiCheckCircle size={14} className="icon-completada" />
            </span>
          </div>
        );

      default:
        return (
          <button
            className="btn btn--sm btn--ghost"
            onClick={(e) => handleEditOrden(e, orden.id)}
            title="Editar orden"
          >
            <FiEdit3 />
          </button>
        );
    }
  }


  function canConvertToInvoice(orden) {
    return ordenUtils.canConvertToInvoice(orden);
  }

  function canRelease(orden) {
    return ordenUtils.canRelease(orden);
  }

  const isPREORDER = typeFilter === 'preordenes';

  if (isLoading && initialLoad) {
    return <LoadingGrid message="Cargando ventas del portal..." />;
  }

  return (
    <div className="ventas-page">
      {/* Mensajes de alerta */}
      {error && (
        <div className="alert alert--danger">
          <FiXCircle className="alert-icon" />
          <div className="alert-content">
            <strong>Error:</strong> {error}
          </div>
        </div>
      )}

      {success && (
        <div className="alert alert--success">
          <FiCheckCircle className="alert-icon" />
          <div className="alert-content">
            {success}
          </div>
        </div>
      )}

      {/* Título */}
      <h1 className="page-title">VENTAS, ESTIMADOS Y PRE-ÓRDENES</h1>

      {/* Menú de acciones */}
      <div className="page-header-with-menu">
        <div className="action-menu" ref={menuRef}>
          {/* Wave SVG dinámica */}
          <svg
            className="action-bar-wave"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 1000 56"
            preserveAspectRatio="none"
            aria-hidden="true"
          >
            <defs>
              <linearGradient id="waveGradVentas" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%"   stopColor="#a64b95" stopOpacity="0.75" />
                <stop offset="45%"  stopColor="#c975b9" stopOpacity="0.75" />
                <stop offset="100%" stopColor="#7e2371" stopOpacity="0.75" />
              </linearGradient>
            </defs>
            <path
              ref={svgPathRef}
              d={canCreateSale
              ? "M 0,3 L 80,3 C 110,3 110,53 140,53 L 500,53 C 530,53 530,3 560,3 L 720,3 C 750,3 750,53 780,53 L 1000,53"
              : "M 0,3 C 0,3 0,53 18,53 L 700,53 C 730,53 730,3 760,3 L 1000,3"}
              fill="none"
              stroke="url(#waveGradVentas)"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>

          {canCreateSale && (
            <button
              className="menu-item menu-item--first"
              ref={sec1Ref}
              onClick={() => setShowOrderTypeModal(true)}
            >
              <span className="add-btn-plus">+</span>
              <span className="add-btn-label">NUEVA ORDEN</span>
            </button>
          )}

          <div className="menu-item menu-item--search" ref={sec2Ref}>
            <input
              type="text"
              className="search-input"
              placeholder="Buscar número de orden o cliente..."
              value={searchQuery}
              onChange={handleSearchChange}
            />
          </div>

          <div className="menu-item menu-item--checkbox" ref={sec3Ref}>
            {/* Panel de filtros contextual al tab activo */}
            <div className="filter-dropdown-wrapper filter-panel-wrapper" ref={sourceDropdownRef}>
              <button
                className={`view-btn filter-btn ${activeFiltersCount > 0 ? 'active' : ''}`}
                onClick={() => setShowSourceDropdown(prev => !prev)}
                title="Abrir panel de filtros"
              >
                <FiFilter />
                {activeFiltersCount > 0 && (
                  <span className="filter-btn-badge">{activeFiltersCount}</span>
                )}
              </button>
              {showSourceDropdown && (() => {
                const panel = (
                <div className={isMobileFilter ? "ventas-mobile-filter-panel filter-panel" : "filter-popover filter-panel"}>
                  <div className="filter-panel-header">
                    <span className="filter-panel-title">Filtros</span>
                    {activeFiltersCount > 0 && (
                      <button
                        type="button"
                        className="filter-panel-clear"
                        onClick={clearAllFilters}
                      >
                        Limpiar
                      </button>
                    )}
                  </div>

                  {/* Selector de tab DENTRO del panel — al cambiar acá también cambian los tabs de la página */}
                  <div className="filter-panel-tabs">
                    {[
                      { id: 'preordenes',   label: 'PRE-ÓRDENES'  },
                      { id: 'ordenes',      label: 'ÓRDENES'      },
                      { id: 'cotizaciones', label: 'COTIZACIONES' },
                      { id: 'todas',        label: 'TODAS'        },
                    ].map(tab => (
                      <button
                        key={tab.id}
                        type="button"
                        className={`filter-panel-tab ${typeFilter === tab.id ? 'active' : ''}`}
                        onClick={() => setTypeFilter(tab.id)}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </div>

                  {/* Fuente */}
                  {activeTabFilters.source && (
                    <div className="filter-panel-section">
                      <label className="filter-panel-label">Fuente</label>
                      <select
                        className="filter-panel-select"
                        value={filterSource}
                        onChange={(e) => setFilterSource(e.target.value)}
                      >
                        {sourceFuentes.map(f => (
                          <option key={f.id} value={f.id}>{f.label}</option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* Estado */}
                  {activeTabFilters.status && statusOptions.length > 0 && (
                    <div className="filter-panel-section">
                      <label className="filter-panel-label">Estado</label>
                      <select
                        className="filter-panel-select"
                        value={filterStatus}
                        onChange={(e) => setFilterStatus(e.target.value)}
                      >
                        <option value="ALL">Todos los estados</option>
                        {statusOptions.map(s => (
                          <option key={s.value} value={s.value}>{s.label}</option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* Rango de fechas */}
                  {activeTabFilters.date && (
                    <div className="filter-panel-section">
                      <label className="filter-panel-label">Rango de fechas</label>
                      <div className="filter-panel-daterange">
                        <input
                          type="date"
                          className="filter-panel-input"
                          value={filterDateFrom}
                          onChange={(e) => setFilterDateFrom(e.target.value)}
                          aria-label="Desde"
                        />
                        <span className="filter-panel-dash">—</span>
                        <input
                          type="date"
                          className="filter-panel-input"
                          value={filterDateTo}
                          onChange={(e) => setFilterDateTo(e.target.value)}
                          aria-label="Hasta"
                        />
                      </div>
                    </div>
                  )}

                  {/* Cliente */}
                  {activeTabFilters.client && (
                    <div className="filter-panel-section">
                      <label className="filter-panel-label">Cliente</label>
                      <input
                        type="text"
                        className="filter-panel-input"
                        placeholder="Buscar por nombre o empresa..."
                        value={filterClient}
                        onChange={(e) => setFilterClient(e.target.value)}
                      />
                    </div>
                  )}
                </div>
                );
                return isMobileFilter ? createPortal(
                  <div className="ventas-mobile-filter-overlay" onClick={() => setShowSourceDropdown(false)}>
                    <div className="ventas-mobile-filter-content" onClick={(e) => e.stopPropagation()}>
                      <button type="button" className="ventas-mobile-filter-close" onClick={() => setShowSourceDropdown(false)} aria-label="Cerrar filtros">×</button>
                      {panel}
                    </div>
                  </div>, document.body
                ) : panel;
              })()}
            </div>

            {/* Exportar */}
            <button className="view-btn" onClick={() => setShowReportModal(true)} title="Descargar reporte">
              <FiDownload />
            </button>
          </div>
        </div>
      </div>

      {/* Tabs de tipo principales */}
      <div className="orden-tabs">
        <button
          className={`orden-tab orden-tab--purple ${typeFilter === 'preordenes' ? 'active' : ''}`}
          onClick={() => setTypeFilter('preordenes')}
        >
          <FiPackage /> PRE-ÓRDENES
        </button>
        <button
          className={`orden-tab orden-tab--blue ${typeFilter === 'ordenes' ? 'active' : ''}`}
          onClick={() => setTypeFilter('ordenes')}
        >
          <FiFileText /> ÓRDENES
        </button>
        <button
          className={`orden-tab orden-tab--green ${typeFilter === 'cotizaciones' ? 'active' : ''}`}
          onClick={() => setTypeFilter('cotizaciones')}
        >
          <FiFileText /> COTIZACIONES
        </button>
        <button
          className={`orden-tab ${typeFilter === 'todas' ? 'active' : ''}`}
          onClick={() => setTypeFilter('todas')}
        >
          <FiShoppingCart /> TODAS
        </button>
      </div>

      {/* Solo mostrar etapas autorizadas para el rol */}
      {isPREORDER && (
        <div className="orden-subtabs">
          {[
            ['detenidas', 'DETENIDAS', <FiPauseCircle />],
            ['procesando', 'PROCESANDO', <FiClock />],
            ['completadas', 'REVISIÓN FINAL', <FiCheckCircle />]
          ].filter(([stage]) => canViewStage(stage)).map(([stage, label, icon]) => (
            <button key={stage}
              className={`orden-subtab ${subFilter === stage ? 'active' : ''}`}
              onClick={() => setSubFilter(stage)}>
              {icon} {label}
            </button>
          ))}
        </div>
      )}

      {/* Chips de filtros activos */}
      {activeFiltersCount > 0 && (
        <div className="filter-chips">
          {activeTabFilters.source && filterSource !== 'ALL' && (
            <span className="filter-chip">
              Fuente: {sourceFuentes.find(f => f.id === filterSource)?.label}
              <button
                type="button"
                className="filter-chip-remove"
                onClick={() => setFilterSource('ALL')}
                aria-label="Quitar filtro de fuente"
              >
                <FiXCircle size={14} />
              </button>
            </span>
          )}
          {activeTabFilters.status && filterStatus !== 'ALL' && (
            <span className="filter-chip">
              Estado: {statusOptions.find(s => s.value === filterStatus)?.label || filterStatus}
              <button
                type="button"
                className="filter-chip-remove"
                onClick={() => setFilterStatus('ALL')}
                aria-label="Quitar filtro de estado"
              >
                <FiXCircle size={14} />
              </button>
            </span>
          )}
          {activeTabFilters.date && (filterDateFrom || filterDateTo) && (
            <span className="filter-chip">
              Fechas: {filterDateFrom || '...'} → {filterDateTo || '...'}
              <button
                type="button"
                className="filter-chip-remove"
                onClick={() => { setFilterDateFrom(''); setFilterDateTo(''); }}
                aria-label="Quitar filtro de fechas"
              >
                <FiXCircle size={14} />
              </button>
            </span>
          )}
          {activeTabFilters.client && filterClient.trim() && (
            <span className="filter-chip">
              Cliente: "{filterClient.trim()}"
              <button
                type="button"
                className="filter-chip-remove"
                onClick={() => setFilterClient('')}
                aria-label="Quitar filtro de cliente"
              >
                <FiXCircle size={14} />
              </button>
            </span>
          )}
          <button
            type="button"
            className="filter-chips-clear"
            onClick={clearAllFilters}
          >
            Limpiar todos
          </button>
        </div>
      )}

      {/* Contenido de ventas */}
      {ordenes.length === 0 && !isLoading && !error ? (
        <div className="empty-state">
          <div className="empty-state-icon">
            <FiShoppingCart size={48} />
          </div>
          <h3 className="empty-state-title">
            {searchQuery ? 'No se encontraron ventas' : 'No hay ventas registradas'}
          </h3>
          <p className="empty-state-description">
            {searchQuery
              ? `No se encontraron ventas que coincidan con "${searchQuery}"`
              : 'Las ventas del portal del cliente aparecerán aquí'
            }
          </p>
          {searchQuery && (
            <p>
              <button
                className="btn btn--ghost"
                onClick={handleClearSearch}
              >
                Limpiar búsqueda
              </button>
            </p>
          )}
        </div>
      ) : isPREORDER ? (
        // Vista especial para PRE-ÓRDENES con acordeón
        <div className="ordenes-table-wrapper">
          <table className="data-table data-table--ordenes">
            <thead>
              <tr>
                <th style={{ width: '50px' }}>VER</th>
                <th>N° ORDEN</th>
                <th>FUENTE</th>
                <th>CLIENTE</th>
                <th>FECHA</th>
                <th className="text-center">PRODUCTOS</th>
                <th className="text-center">ACCIONES</th>
              </tr>
            </thead>
            <tbody>
              {ordenes.map((orden) => (
                <React.Fragment key={orden.id}>
                  {/* Fila principal */}
                  <tr
                    className={`orden-row ${expandedOrderId === orden.id ? 'expanded' : ''} ${orden.source === 'PORTAL' ? 'orden-row--portal' : ''}`}
                  >
                    <td className="text-center">
                      <button
                        type="button"
                        className="btn-expand"
                        onClick={() => toggleExpand(orden.id)}
                        title="Ver detalle"
                      >
                        {expandedOrderId === orden.id ? (
                          <FiChevronDown size={18} className="expand-icon" />
                        ) : (
                          <FiChevronRight size={18} className="expand-icon" />
                        )}
                      </button>
                    </td>
                    <td>
                      <strong>{orden.number || `#${orden.id}`}</strong>
                    </td>
                    <td>{getSourceBadge(orden.source)}</td>
                    <td>
                      <strong>{orden.client?.company_name || orden.client?.full_name || `ID: ${orden.client_id}`}</strong>
                    </td>
                    <td>
                      {formatDate(orden.sale_date || orden.estimate_date || orden.created_at)}
                    </td>
                    <td className="text-center">
                      <span className="items-badge">
                        {orden.items_count || orden.items?.length || 0}
                      </span>
                    </td>
                    <td className="text-center">
                      {getActionButton(orden)}
                    </td>
                  </tr>

                  {/* Fila expandida (detalle) */}
                  {expandedOrderId === orden.id && (
                    <tr className="orden-detail-row">
                      <td colSpan="7">
                        {loadingDetails ? (
                          <div className="orden-detail-content">
                            <div className="loading-details">
                              <FiClock size={24} className="loading-icon" />
                              <p>Cargando detalles de la orden...</p>
                            </div>
                          </div>
                        ) : (
                          <div className="orden-detail-content">
                            {(() => {
                              const detailData = expandedOrderDetails[orden.id] || orden;
                              return (
                                <>
                                  {/* Información General */}
                                  <div className="detail-section">
                                    <h4 className="detail-section-title">
                                      <FiUser size={16} />
                                      Información General
                                    </h4>
                                    <div className="detail-grid">
                                      <div className="detail-field">
                                        <label>Cliente:</label>
                                        <span>{detailData.client?.company_name || detailData.client?.full_name || 'N/A'}</span>
                                      </div>
                                      <div className="detail-field">
                                        <label>Contacto:</label>
                                        <span>{detailData.client?.full_name || 'N/A'}</span>
                                      </div>
                                      <div className="detail-field">
                                        <label>Teléfono:</label>
                                        <span>{detailData.client?.phone || 'N/A'}</span>
                                      </div>
                                      <div className="detail-field">
                                        <label>Email:</label>
                                        <span>{detailData.client?.email || 'N/A'}</span>
                                      </div>
                                      <div className="detail-field">
                                        <label>Fecha de Orden:</label>
                                        <span>{formatDate(detailData.sale_date || detailData.created_at)}</span>
                                      </div>
                                      <div className="detail-field">
                                        <label>Tomada por:</label>
                                        <span>{detailData.taken_by || 'N/A'}</span>
                                      </div>
                                      {detailData.order_type === 'ORDER' && (
                                        <div className="detail-field">
                                          <label>Revisada por:</label>
                                          <span>{detailData.reviewed_by || 'N/A'}</span>
                                        </div>
                                      )}
                                    </div>
                                  </div>

                                  {/* Productos */}
                                  <div className="detail-section">
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                                      <h4 className="detail-section-title" style={{ marginBottom: 0 }}>
                                        <FiPackage size={16} />
                                        Productos ({detailData.items?.length || 0})
                                      </h4>
                                      {detailData.order_type === 'PRE_ORDER' && detailData.status === 'PROCESSING' && (
                                        <span style={{ fontSize: 12, color: '#6f258b', fontWeight: 600 }} title={(processingParticipants[orden.id] || []).map(p => p.name).join(', ')}>
                                          ● {(processingParticipants[orden.id] || []).length} persona(s) trabajando en esta orden
                                          {(processingParticipants[orden.id] || []).length > 0 && ': ' + (processingParticipants[orden.id] || []).map(p => p.name).join(', ')}
                                        </span>
                                      )}
                                      {canManageOrder(detailData) && detailData.order_type === 'PRE_ORDER' && detailData.status === 'PROCESSING' && (
                                        <button
                                          className="btn-validar-todos"
                                          onClick={() => handleValidateAllItems(orden.id)}
                                          disabled={loadingDetails}
                                        >
                                          <FiCheckCircle size={14} /> Validar Todos Automáticamente
                                        </button>
                                      )}
                                    </div>
                                    {!detailData.items || detailData.items.length === 0 ? (
                                      <p className="no-items-text">No hay productos en esta orden</p>
                                    ) : (
                                      <div className="productos-detail-list">
                                        {pickingItems(detailData.items, detailData.status).map((item, index, orderedItems) => (
                                          <React.Fragment key={item.id || index}>
                                            {['PROCESSING', 'COMPLETED'].includes(detailData.status) && (index === 0 || categoryInfo(orderedItems[index - 1].product_category)[0] !== categoryInfo(item.product_category)[0]) && (
                                              <div className="picking-category-header" style={{ color: categoryInfo(item.product_category)[2], background: categoryInfo(item.product_category)[3], borderLeft: `5px solid ${categoryInfo(item.product_category)[2]}`, padding: "9px 12px", marginTop: index ? 12 : 0, fontWeight: 700, borderRadius: 5 }}>
                                                {categoryInfo(item.product_category)[1]}
                                              </div>
                                            )}
                                          <div className="producto-detail-item" style={['PROCESSING', 'COMPLETED'].includes(detailData.status) ? { borderLeft: `4px solid ${categoryInfo(item.product_category)[2]}`, paddingLeft: 10 } : undefined}>
                                            <div className="item-number">{index + 1}</div>
                                            <div className="item-info">
                                              <div className="item-name">
                                                <strong>{item.description || item.product_name}</strong>
                                              </div>
                                              <div className="item-meta">
                                                <span>Código: {item.product_code || 'N/A'}</span>

                                                {/* Para PRE_ORDER mostrar cantidades solicitadas y confirmadas */}
                                                {detailData.order_type === 'PRE_ORDER' ? (
                                                  <span>
                                                    Solicitado: <strong>{item.quantity_requested || item.quantity}</strong>
                                                    {detailData.status === 'PROCESSING' ? (
                                                      <> | Confirmado:
                                                        <input
                                                          type="number"
                                                          className="quantity-confirm-input"
                                                          value={tempQuantities[`${orden.id}-${item.id}`] ?? item.quantity_confirmed ?? item.quantity_requested ?? item.quantity}
                                                          min="0"
                                                          max={item.quantity_requested ?? item.quantity}
                                                          step="0.1"
                                                          onChange={(e) => handleQuantityConfirmedChange(orden.id, item.id, e.target.value)}
                                                          onBlur={(e) => handleQuantityConfirmedBlur(orden.id, item.id, e.target.value)}
                                                          onClick={(e) => e.stopPropagation()}
                                                          disabled={!canManageOrder(detailData)}
                                                        />
                                                      </>
                                                    ) : (
                                                      item.quantity_confirmed && (
                                                        <> | Confirmado: <strong className="confirmed-qty">{item.quantity_confirmed}</strong></>
                                                      )
                                                    )}
                                                  </span>
                                                ) : (
                                                  <span>Cantidad: <strong>{item.quantity}</strong></span>
                                                )}

                                                {/* Botones de validación para PRE_ORDER */}
                                                {detailData.order_type === 'PRE_ORDER' && (
                                                  <div className="item-validation-buttons">
                                                    {getItemValidationButtons(item.validation_status, item.id, orden.id, item)}
                                                  </div>
                                                )}

                                                {/* Iconos de stock para otros tipos */}
                                                {detailData.order_type !== 'PRE_ORDER' && item.is_stock_available !== null && item.is_stock_available !== undefined && (
                                                  <span className="item-status">
                                                    {getItemStatusIcon(item)}
                                                  </span>
                                                )}
                                              </div>

                                              {/* Notas de validación */}
                                              {/* Los marcadores ESTADO_PROCESANDO son internos, no notas visibles. */}
                                              {item.validation_notes?.replace(/\[ESTADO_PROCESANDO:(?:PENDING|NO_STOCK|AVAILABLE)\]/g, '').trim() && (
                                                <div className="item-notes validation-notes">
                                                  <small><strong>Validación:</strong> {item.validation_notes.replace(/\[ESTADO_PROCESANDO:(?:PENDING|NO_STOCK|AVAILABLE)\]/g, '').trim()}</small>
                                                </div>
                                              )}
                                              {item.notes && (
                                                <div className="item-notes">
                                                  <small>Nota: {item.notes}</small>
                                                </div>
                                              )}
                                            </div>
                                          </div>
                                          </React.Fragment>
                                        ))}
                                      </div>
                                    )}
                                  </div>

                                  {/* Mantener intactas las notas originales y los avisos automáticos sin stock. */}
                                  {Boolean(detailData.notes?.trim()) && (
                                    <div className="detail-section">
                                      <h4 className="detail-section-title">Notas de la Orden</h4>
                                      <p className="orden-notes-text">{detailData.notes}</p>
                                    </div>
                                  )}
                                  {(Boolean(detailData.internal_notes?.trim()) || editingInternalNotes[orden.id] || canManageOrder(detailData)) && (
                                    <div className="detail-section">
                                      {editingInternalNotes[orden.id] && canManageOrder(detailData) ? (
                                        <>
                                          <h4 className="detail-section-title">Notas Internas</h4>
                                          <textarea
                                            className="invoice-field-input invoice-field-textarea"
                                            aria-label="Editar Notas Internas"
                                            rows={3}
                                            value={internalNotesDrafts[orden.id] ?? detailData.internal_notes ?? ''}
                                            onChange={(e) => setInternalNotesDrafts(prev => ({ ...prev, [orden.id]: e.target.value }))}
                                            disabled={savingInternalNotes === orden.id}
                                          />
                                          <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                                            <button
                                              type="button"
                                              className="btn-validar-todos"
                                              disabled={savingInternalNotes === orden.id}
                                              onClick={() => saveInternalStageNotes(detailData)}
                                            >
                                              {savingInternalNotes === orden.id ? 'Guardando...' : 'Guardar Notas Internas'}
                                            </button>
                                            <button
                                              type="button"
                                              className="btn-validar-todos"
                                              disabled={savingInternalNotes === orden.id}
                                              onClick={() => setEditingInternalNotes(prev => ({ ...prev, [orden.id]: false }))}
                                            >Cancelar</button>
                                          </div>
                                        </>
                                      ) : (
                                        <>
                                          {Boolean(detailData.internal_notes?.trim()) && (
                                            <>
                                              <h4 className="detail-section-title">Notas Internas</h4>
                                              <p className="orden-notes-text">{detailData.internal_notes}</p>
                                            </>
                                          )}
                                          {canManageOrder(detailData) && (
                                            <button
                                              type="button"
                                              className="btn-validar-todos"
                                              onClick={() => {
                                                setInternalNotesDrafts(prev => ({ ...prev, [orden.id]: detailData.internal_notes || '' }));
                                                setEditingInternalNotes(prev => ({ ...prev, [orden.id]: true }));
                                              }}
                                            >
                                              <FiEdit3 size={14} /> {detailData.internal_notes?.trim() ? 'Editar Notas Internas' : 'Agregar Notas Internas'}
                                            </button>
                                          )}
                                        </>
                                      )}
                                    </div>
                                  )}
                                </>
                              );
                            })()}
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        // Vista tabla para ORDER y ESTIMATE
        <div className="table-container">
          <table className="ventas-table">
            <thead>
              <tr>
                <th>TIPO</th>
                <th>N° ORDEN</th>
                <th>CLIENTE</th>
                <th>FECHA</th>
                <th>ITEMS</th>
                {typeFilter === 'ordenes' && (
                  <>
                    <th>TOMADA POR</th>
                    <th>REVISADA POR</th>
                  </>
                )}
                <th>TOTAL</th>
                <th>ESTADO</th>
                <th>ACCIONES</th>
              </tr>
            </thead>
            <tbody>
              {ordenes.map((orden) => {
                const isProcessing = processingOrders.has(orden.id);

                return (
                  <tr
                    key={orden.id}
                    onClick={() => handleViewOrder(orden)}
                    className="table-row-clickable"
                  >
                    <td className="tipo-cell">
                      {getTipoBadge(orden.type)}
                    </td>
                    <td className="orden-number-cell">
                      {orden.number || `#${orden.id}`}
                    </td>
                    <td className="client-cell">
                      <div className="client-info">
                        <div className="client-name">
                          {orden.client?.company_name || orden.client?.full_name || `Cliente ID: ${orden.client_id}`}
                        </div>
                        {orden.title && (
                          <div className="orden-title">{orden.title}</div>
                        )}
                      </div>
                    </td>
                    <td className="date-cell">
                      <FiCalendar size={14} className="icon-inline" />
                      {formatDate(orden.sale_date || orden.created_at)}
                    </td>
                    <td className="items-cell">
                      <FiPackage size={14} className="icon-inline" />
                      {orden.items?.length || 0}
                    </td>
                    {typeFilter === 'ordenes' && (
                      <>
                        <td>{orden.taken_by || '-'}</td>
                        <td>{orden.reviewed_by || '-'}</td>
                      </>
                    )}
                    <td className="total-cell">
                      {formatCurrency(orden.total)}
                    </td>
                    <td className="status-cell">
                      {getEstadoBadge(orden.status)}
                    </td>
                    <td className="actions-cell" onClick={(e) => e.stopPropagation()}>
                      <div className="table-actions">
                        {canManageOrder(orden) && canApprove(orden) && (
                          <>
                            <button
                              className="btn-action btn-action--success"
                              onClick={(e) => handleApprove(e, orden)}
                              disabled={isProcessing}
                              title="Aprobar venta"
                            >
                              <FiCheckCircle />
                            </button>
                            <button
                              className="btn-action btn-action--danger"
                              onClick={(e) => handleReject(e, orden)}
                              disabled={isProcessing}
                              title="Rechazar venta"
                            >
                              <FiXCircle />
                            </button>
                          </>
                        )}

                        {canManageOrder(orden) && orden.order_type === 'PRE_ORDER' && ['ON_HOLD', 'APPROVED'].includes(orden.status) && (
                          <button className="btn-action btn-action--release" onClick={(e) => handleEnProceso(e, orden)} disabled={isProcessing} title="Pasar a Procesando"><FiClock /></button>
                        )}

                        {canManageOrder(orden) && canConvertToInvoice(orden) && (
                          <button
                            className="btn-action btn-action--primary"
                            onClick={(e) => handleConvertToInvoice(e, orden)}
                            disabled={isProcessing}
                            title="Convertir a factura"
                          >
                            <FiFileText />
                          </button>
                        )}

                        <button
                          className="btn-action btn-action--view"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewOrder(orden);
                          }}
                          title="Ver detalle"
                        >
                          <FiEye />
                        </button>

                        {canDeleteSale && (
                          <button
                            className="btn-action btn-action--danger"
                            onClick={(e) => handleDeleteOrden(e, orden)}
                            disabled={isProcessing}
                            title="Eliminar orden"
                          >
                            <FiTrash2 />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Paginación */}
      {!isLoading && totalItems > 0 && (
        <div className="ventas-pagination">
          <button
            type="button"
            className="pagination-icon-btn"
            onClick={() => changePage(page - 1)}
            disabled={page <= 1}
            title="Página anterior"
            aria-label="Página anterior"
          >
            <FiChevronLeft size={20} />
          </button>
          <span className="ventas-pagination__info">
            Página {page} de {totalPages} · {totalItems} registro(s)
          </span>
          <button
            type="button"
            className="pagination-icon-btn"
            onClick={() => changePage(page + 1)}
            disabled={page >= totalPages}
            title="Página siguiente"
            aria-label="Página siguiente"
          >
            <FiChevronRight size={20} />
          </button>
        </div>
      )}

      {/* Modal de descarga de reportes */}
      {showReportModal && (
        <DownloadReportModal
          isOpen={showReportModal}
          onClose={() => setShowReportModal(false)}
          reportType="ventas"
          title="Descargar Reporte de Ventas y Estimados"
          isLoading={isDownloadingReport}
          onDownload={async ({ startDate, endDate, format }) => {
            // Antes el modal no recibía onDownload y el botón no hacía nada.
            setIsDownloadingReport(true);
            try {
              const result = await reportesService.exportarVentas({
                format,
                date_from: startDate || undefined,
                date_to: endDate || undefined
              });
              if (!result.success) {
                throw new Error(result.message || 'Error al generar el reporte');
              }
              const mimeTypes = { csv: 'text/csv', pdf: 'application/pdf', excel: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' };
              const extensions = { csv: 'csv', pdf: 'pdf', excel: 'xlsx' };
              const blob = new Blob([result.data], { type: mimeTypes[format] || 'application/octet-stream' });
              const url = window.URL.createObjectURL(blob);
              const link = document.createElement('a');
              link.href = url;
              link.download = startDate && endDate
                ? `ventas_estimados_${startDate}_${endDate}.${extensions[format]}`
                : `ventas_estimados_${new Date().toISOString().split('T')[0]}.${extensions[format]}`;
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              window.URL.revokeObjectURL(url);
              setShowReportModal(false);
            } catch (error) {
              console.error('Error al descargar reporte de ventas:', error);
              alert('Error al descargar el reporte. Por favor intente nuevamente.');
            } finally {
              setIsDownloadingReport(false);
            }
          }}
        />
      )}

      {/* Modal de revisión para convertir a ORDER */}
      <Modal
        isOpen={showReviewModal}
        onClose={() => {
          if (!isConverting) {
            setShowReviewModal(false);
            setReviewerName('');
            setOrderToConvert(null);
          }
        }}
        title="Convertir PRE-ORDER a ORDER"
        size="medium"
        variant="info"
        closeOnOverlay={!isConverting}
        closeOnEscape={!isConverting}
        showCloseButton={!isConverting}
      >
        <div style={{ padding: '20px' }}>
          <div style={{ marginBottom: '20px' }}>
            <p style={{ marginBottom: '15px', color: '#4b5563', lineHeight: '1.6' }}>
              {orderToConvert && (
                <>
                  Está a punto de convertir la <strong>PRE-ORDER {orderToConvert.number}</strong> a una ORDER.
                  <br />
                  Se aplicarán las cantidades confirmadas por shipping y se descartarán
                  los ítems sin stock. Después de convertirla podrá facturarla.
                </>
              )}
            </p>
          </div>

          <div style={{ marginBottom: '25px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#374151' }}>
              ¿Quién revisó la orden? *
            </label>
            <input
              type="text"
              value={reviewerName}
              onChange={(e) => setReviewerName(e.target.value)}
              placeholder="Nombre completo de quien revisó"
              disabled={isConverting}
              autoFocus
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '2px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                transition: 'border-color 0.2s',
                outline: 'none'
              }}
              onFocus={(e) => e.target.style.borderColor = '#7B2D8E'}
              onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
            />
            <small style={{ display: 'block', marginTop: '6px', color: '#6b7280', fontSize: '13px' }}>
              Ingrese el nombre de la persona que validó y revisó todos los productos de esta pre-orden
            </small>
          </div>

          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
            <button
              type="button"
              className="btn btn--outline"
              onClick={() => {
                setShowReviewModal(false);
                setReviewerName('');
                setOrderToConvert(null);
              }}
              disabled={isConverting}
              style={{ minWidth: '100px' }}
            >
              Cancelar
            </button>
            <button
              type="button"
              className="btn btn--primary"
              onClick={handleConfirmConversion}
              disabled={isConverting || !reviewerName.trim()}
              style={{ minWidth: '150px' }}
            >
              {isConverting ? 'Convirtiendo...' : 'Convertir a ORDER'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Modal de selección de tipo de orden */}
      <OrderTypeModal
        isOpen={showOrderTypeModal}
        onClose={() => setShowOrderTypeModal(false)}
      />

      {/* Confirmación genérica de acciones */}
      {confirm && (
        <ConfirmModal
          isOpen={true}
          title={confirm.title}
          message={confirm.message}
          confirmText={confirm.confirmText || 'Confirmar'}
          confirmClass={confirm.confirmClass || 'btn-primary'}
          showReason={confirm.withReason}
          reasonValue={confirmReason}
          onReasonChange={setConfirmReason}
          reasonLabel={confirm.reasonLabel || 'Motivo (opcional)'}
          onConfirm={acceptConfirm}
          onCancel={() => { setConfirm(null); setConfirmReason(''); }}
        />
      )}
    </div>
  );
};

export default Ventas;
